import {Directive, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {FieldTableDto} from '@tj/tj-dmp-lib-api';
import {ValueEditor} from './value-editor';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ValidatorEditorService} from './validator-editor.service';
import {Subscription} from 'rxjs/internal/Subscription';

@Directive()
export abstract class AbstractEditorComponent implements OnDestroy {
    @Input() field: FieldTableDto;
    @Input() value: any;
    @Input() cellEditor: any;
    // Bouton d'enregistrement
    @Input() displayButton = true;
    @Input() importMode = false;

    @Output() valueEditorChange = new EventEmitter<ValueEditor>();
    @Output() valueChange = new EventEmitter<any>();
    @Output() onInitReady = new EventEmitter<AbstractEditorComponent>();
    @Output() dataNotFound = new EventEmitter<FieldTableDto>();

    originalValue: any;
    formGroup: FormGroup = null;
    disable = false;
    display: string = null;
    displayHover: string = null;
    valueEditor: ValueEditor;
    isEditing = false;
    wasExplicitlySaved = false;

    private readonly subList: Subscription[] = [];

    protected constructor(
        protected fb: FormBuilder,
        protected validatorService: ValidatorEditorService) {
    }

    isValid() {
        return this.formGroup.valid && this.validatorService.doValidate(this.field, this.formGroup.value.value);
    }

    isDirty() {
        return this.formGroup.dirty;
    }

    save(event?) {
        let formValue = this.formGroup.get('value')?.value;
        // `formValue === 'string'` comparait la valeur au littéral "string" : le trim ne s'exécutait
        // jamais et les espaces de début et de fin arrivaient jusqu'en base.
        let value = (typeof formValue === 'string' && formValue != null) ? formValue.trim() : formValue;
        if (this.isValid()){
            value = (value !== null && value !== undefined && value !== "") ? String(this.transformBeforeSave(value)) : null;
            this.originalValue = value;
            this.valueChange.emit(value);
            this.isEditing = false;
            this.wasExplicitlySaved = true; 
            if (this.cellEditor) {
                setTimeout(() => {
                    this.cellEditor.editableColumn.closeEditingCell();
                }, 0);
            } else {
                this.formGroup.markAsPristine();
            }
        }
    }

    /**
     * retourne la valeur
     * Si non valide lance une erreur
     */
    getValueWithException(): object {
        let formValue = this.formGroup.get('value')?.value;
        let value = (formValue === 'string') ? formValue.trim() : formValue;
        if (this.isValid()){
            value = (value !== null && value !== undefined && value !== "") ? String(this.transformBeforeSave(value)) : null;
            return value;
        } else {
            throw new Error('not valid');
        }
    }

    cancel() {
        this.formGroup.get('value').setValue(this.originalValue, { emitEvent: false });
        this.formGroup.markAsPristine();
        this.isEditing = false;
        this.wasExplicitlySaved = true;
        this.formGroup.reset();
        if (this.cellEditor) {
            setTimeout(() => {
                this.cellEditor.editableColumn.closeEditingCell();
            }, 0);
        }
    }


    protected init() {
        this.onInitReady.emit(this);
        this.originalValue = this.value;
        this.valueEditor = {value: this.value, status: true};
        this.isEditing = true;
        const validator = [];
        if (this.field.notNull) {
            validator.push(Validators.required);
        }
        if (this.field.categoryField === 'EMAIL') {
            validator.push(Validators.email);
        }
        const disabledControl = this.field.categoryField === 'TECHNICAL';
        this.formGroup = this.fb.nonNullable.group({
            value: [{value: this.value, disabled: disabledControl}, validator ],
        });

        // Emit valueChange when form value changes (if displayButton is false)
        if (!this.displayButton) {
            const valueChangesSub = this.formGroup.statusChanges.subscribe(() => {
                const newValue = this.formGroup.get("value").value;
                if (this.isValid()) {
                    const processedValue = (newValue !== null && newValue !== undefined && newValue !== "")
                        ? this.transformBeforeSave(newValue)
                        : null;
                    this.valueChange.emit(processedValue);
                }
            });
            if (valueChangesSub) {
                this.subList.push(valueChangesSub);
            }
        }
    }

    abstract transformBeforeSave(value: any);

    ngOnDestroy(): void {
        if (!this.wasExplicitlySaved && this.formGroup && this.formGroup.dirty) {
            Promise.resolve().then(() => {
                this.valueChange.emit(this.originalValue);
            });
        }
        this.subList.forEach(sub => sub.unsubscribe());
    }

}
